import React from 'react'
import { Bullet, Bullets, Info, Page, Section } from '../../components/DocsComponents'
import DocLink from '../../components/DocLink'

const ClinicalModules = () => (
  <Page
    title="Clinical Assessment and Treatment Modules"
    intro="A full ePCR is built from a set of assessment and treatment modules. Which modules are relevant depends on the patient's presentation — not every module is used on every record."
  >
    <Section title="Assessment modules">
      <p>
        The Assess step offers the following modules. Each entry is
        timestamped, and multiple entries of the same module can be recorded
        on one patient.
      </p>
      <Bullets>
        <Bullet>Primary survey</Bullet>
        <Bullet>Respiratory</Bullet>
        <Bullet>Cardiovascular</Bullet>
        <Bullet>ECG</Bullet>
        <Bullet>Neurological</Bullet>
        <Bullet>Gastrointestinal</Bullet>
        <Bullet>Musculoskeletal, plus a separate head injury module</Bullet>
        <Bullet>Eyes</Bullet>
        <Bullet>Trauma (including C-spine assessment)</Bullet>
        <Bullet>Skin</Bullet>
        <Bullet>ENT (ears, nose, and throat)</Bullet>
        <Bullet>Wound assessment</Bullet>
        <Bullet>Mental capacity</Bullet>
        <Bullet>Mental health</Bullet>
        <Bullet>Point of care testing (POC) — see below</Bullet>
        <Bullet>
          Imaging — when enabled for your organisation, imaging studies are
          requested and tracked from here; see{' '}
          <DocLink
            docsPage
            url="/clinical-records/imaging-requests"
            text="Imaging Requests"
          />
        </Bullet>
        <Bullet>
          Safeguarding — see{' '}
          <DocLink
            docsPage
            url="/clinical-records/safeguarding-and-health-safety"
            text="Safeguarding and Health & Safety"
          />
        </Bullet>
        <Bullet>Body maps for marking injuries on a medical drawing</Bullet>
        <Bullet>Free-text assessment notes</Bullet>
      </Bullets>
    </Section>

    <Section title="Point of care testing (POC)">
      <p>
        The <strong>POC</strong> module records bedside test results as
        timestamped assessment entries. Pick the test and the form shows the
        right fields for it:
      </p>
      <Bullets>
        <Bullet>
          Pregnancy (hCG), COVID-19, Influenza A/B, and Strep A — recorded as
          Positive, Negative, or Invalid (with Flu A/B distinguished).
        </Bullet>
        <Bullet>
          Urinalysis — the full dipstick panel (leukocytes, nitrites,
          protein, blood, ketones, glucose, bilirubin, urobilinogen) with
          graded results, plus pH and specific gravity.
        </Bullet>
        <Bullet>
          Blood gas — sample type (arterial, venous, or capillary) with pH,
          pCO2, pO2, HCO3, base excess, lactate, and glucose.
        </Bullet>
        <Bullet>
          Cardiac markers — Troponin I/T, BNP / NT-proBNP, or CK-MB with a
          result and a value including units.
        </Bullet>
        <Bullet>
          Blood ketones, D-dimer, INR, and haemoglobin values — and an
          "Other" option for any test not listed.
        </Bullet>
      </Bullets>
      <p>
        Every test can also record the device or test-kit lot number, which
        is worth capturing for point-of-care quality control. Record one
        entry per test performed — a patient can have any number of POC
        entries.
      </p>
      <Info title="Available on mobile too">
        The POC module is available in the mobile app's Assess tab with the
        same tests and fields, and works offline like the rest of the mobile
        ePCR.
      </Info>
    </Section>

    <Section title="Vital signs, NEWS2 and PEWS">
      <p>
        The Vitals step records timed observation sets: respiratory rate,
        SpO2, supplemental oxygen, blood pressure, heart rate, capillary
        refill time (CRT), temperature, blood glucose, GCS, AVPU, pain score,
        ETCO2, and PEFR.
      </p>
      <Bullets>
        <Bullet>
          For adults, a NEWS2 score is calculated automatically for each
          observation set once all the required observations are present —
          respiratory rate, SpO2, supplemental oxygen, systolic blood
          pressure, heart rate, AVPU, and temperature — and displayed with
          the standard colour banding.
        </Bullet>
        <Bullet>
          If the patient is flagged in PMHx as using SpO2 Scale 2 (for
          example, hypercapnic respiratory failure), the NEWS2 calculation
          uses Scale 2 automatically.
        </Bullet>
        <Bullet>
          For patients under 18 the column becomes a PEWS score instead,
          following the NHS England National PEWS charts with age-appropriate
          thresholds (0–11 months, 1–4, 5–12, and 13–17 years) from the
          recorded date of birth — or from an estimated age group selected on
          the obs set when the DOB is unknown. The observation popup groups
          the required PEWS observations and asks the additional questions:
          respiratory distress (none/mild/moderate/severe); when on
          supplemental oxygen, the delivered concentration (%) and whether
          the patient is on High Flow, CPAP, or BiPAP; and, when no BP could
          be recorded, the reason (not attempted or unsuccessful — an
          unsuccessful attempt with concern scores 4). PEWS also requires
          CRT, scores each parameter 0, 1, 2, or 4, and flags temperature
          ≥38°C (red) or ≤36°C (blue) without scoring it.
        </Bullet>
      </Bullets>
      <Info title="Same scoring on mobile">
        The mobile app records the same observation fields (including CRT)
        and uses the same NEWS2 and PEWS calculations as the web app.
      </Info>
    </Section>

    <Section title="Treatment modules">
      <Bullets>
        <Bullet>Medicines</Bullet>
        <Bullet>Airway</Bullet>
        <Bullet>Oxygen</Bullet>
        <Bullet>Entonox</Bullet>
        <Bullet>IV/IO access</Bullet>
        <Bullet>IV/IO fluids, recorded separately from vascular access</Bullet>
        <Bullet>Catastrophic haemorrhage control, including tourniquet use</Bullet>
        <Bullet>Splinting</Bullet>
        <Bullet>Sedation</Bullet>
        <Bullet>Cardiac arrest</Bullet>
        <Bullet>Wound closure</Bullet>
      </Bullets>
      <Info title="Fields vary by organisation">
        The exact fields visible in a record can vary by workflow, patient
        presentation, and organisation settings. Fast Track records offer the
        Medicines and Wound Closure treatment modules only.
      </Info>
    </Section>

    <Section title="Recording medicines">
      <p>
        The Medicines module offers three ways to record a drug being given:
      </p>
      <Bullets>
        <Bullet>
          <strong>From a drug bag</strong> — select the medicine from a bag
          you hold, so stock levels and the administration log stay in step
          with the Medicines Management module.
        </Bullet>
        <Bullet>
          <strong>Drawn-up syringe</strong> — give a medicine you prepared
          earlier from your drug bag (common in critical care). The entry
          links back to the drawn-up preparation, pre-filling the drug, dose,
          and batch.
        </Bullet>
        <Bullet>
          <strong>Record manually</strong> — free-entry for medicines given
          outside tracked stock, such as a patient&apos;s own medication.
        </Bullet>
      </Bullets>
      <Info title="Corrections while in Draft">
        Tracked administrations can be corrected or voided while the record is
        still a draft — see{' '}
        <DocLink
          docsPage
          url="/clinical-records/starting-and-finding"
          text="Starting and Finding ePCRs"
        />
        . Submitted records keep their medicine entries fixed.
      </Info>
    </Section>

    <Section title="Critical Care live consoles">
      <p>
        Critical Care records add tap-to-log live consoles on top of the full
        module set — for cardiac arrest, trauma, emergency anaesthesia and
        other time-critical workflows. A single tap logs an intervention with
        its timestamp during the job; each tap becomes a normal assessment or
        treatment entry, with a badge counting the entries still waiting for
        their full details to be completed afterwards. A cardiac arrest logged
        on any record type opens the dedicated arrest console. See{' '}
        <DocLink
          docsPage
          url="/clinical-records/starting-and-finding"
          text="Starting and Finding ePCRs"
        />{' '}
        for the Critical Care record type itself.
      </p>
    </Section>
  </Page>
)

export default ClinicalModules
